import { useState, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { TEAM_COLORS, TEAM_ICONS } from '../data/teamData';
import { Av } from './shared';
import * as api from '../services/api';

/**
 * Create or Edit subteam modal.
 * Pass `subteam` prop to enter edit mode; omit for create mode.
 */
export const ModalNewSubteam = memo(({onClose, onSave, subteam = null}) => {
  const isEdit = !!subteam;

  const [form, setForm] = useState({
    name: subteam?.name || "",
    description: subteam?.description || "",
    color: subteam?.color || "#0891b2",
    icon: subteam?.icon || "🏆",
    lead: subteam?.lead || "",
    territory: subteam?.territory || "",
    status: subteam?.status || "active",
    period_start: subteam?.period_start || "",
    period_end: subteam?.period_end || "",
    target_taps: subteam?.target_taps || 0,
    target_connects: subteam?.target_connects || 0,
    target_saves: subteam?.target_saves || 0,
    target_views: subteam?.target_views || 0,
  });
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [existingMembers, setExistingMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const setNum = (k,v) => {
    const n = parseInt(v, 10);
    set(k, isNaN(n) || n < 0 ? 0 : n);
  };

  useEffect(() => {
    // Load available profiles (not in any subteam, or in current subteam for edit)
    api.getAvailableSubteamProfiles(isEdit ? subteam.id : null).then(res => {
      const data = res.data || res;
      const items = Array.isArray(data) ? data : [];
      setMembers(items);

      // If editing, pre-select profiles already in this subteam
      if (isEdit && subteam.id) {
        api.getSubteamProfiles(subteam.id).then(res2 => {
          const d = res2.data || res2;
          const inTeam = d.in_subteam || [];
          const ids = inTeam.map(p => p.id);
          setSelectedMembers(ids);
          setExistingMembers(ids);
        }).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const toggleMember = (id) =>
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);

  const filteredMembers = members.filter(m => {
    const name = (m.name || '').toLowerCase();
    const email = (m.email || '').toLowerCase();
    return name.includes(memberSearch.toLowerCase()) || email.includes(memberSearch.toLowerCase());
  });

  const handleSave = async () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (form.name.trim().length > 0 && form.name.trim().length < 3) e.name = "Min 3 characters";
    if (form.period_start && form.period_end && form.period_end < form.period_start) e.period_end = "Must be after start date";
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        icon: form.icon,
        color: form.color,
        lead: form.lead || null,
        territory: form.territory || null,
        status: form.status,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        target_taps: form.target_taps,
        target_connects: form.target_connects,
        target_saves: form.target_saves,
        target_views: form.target_views,
      };

      let teamId;
      if (isEdit) {
        const res = await api.updateSubteam(subteam.id, payload);
        teamId = subteam.id;
      } else {
        const res = await api.createSubteam(payload);
        const newTeam = res.data || res;
        teamId = newTeam.id;
      }

      // Sync members — add new, remove removed
      if (teamId) {
        const toAdd = selectedMembers.filter(id => !existingMembers.includes(id));
        const toRemove = existingMembers.filter(id => !selectedMembers.includes(id));

        for (const profileId of toAdd) {
          try { await api.addProfileToSubteam(teamId, profileId); } catch {}
        }
        for (const profileId of toRemove) {
          try { await api.removeProfileFromSubteam(teamId, profileId); } catch {}
        }
      }

      onSave({ id: teamId, ...payload });
      onClose();
    } catch (err) {
      let msg = err.message || `Failed to ${isEdit ? 'update' : 'create'} subteam`;
      if (err.errors) { const first = Object.values(err.errors)[0]; msg = Array.isArray(first) ? first[0] : first; }
      setErrors({ api: msg });
    } finally { setSaving(false); }
  };

  const SectionLabel = ({children}) => (
    <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:10,marginTop:18}}>{children}</div>
  );

  return createPortal(
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget) onClose();}}>
      <div style={{width:"100%",maxWidth:560,maxHeight:"85vh",background:"rgba(255,255,255,.97)",backdropFilter:"blur(40px)",borderRadius:22,boxShadow:"0 32px 80px rgba(0,0,0,.18),0 8px 32px rgba(0,0,0,.1)",display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* Header */}
        <div style={{padding:"22px 24px 0",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:44,height:44,borderRadius:12,background:`${form.color}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:`0 4px 14px ${form.color}22`}}>{form.icon}</div>
              <div>
                <div style={{fontSize:17,fontWeight:720,color:"#0f172a",letterSpacing:"-.3px"}}>{isEdit ? 'Edit Subteam' : 'Create New Subteam'}</div>
                <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>{isEdit ? 'Update subteam details and members' : 'Set name, targets, members and team identity'}</div>
              </div>
            </div>
            <button onClick={onClose}
              style={{width:32,height:32,borderRadius:10,border:"1px solid rgba(0,0,0,.1)",background:"rgba(0,0,0,.04)",cursor:"pointer",fontSize:14,color:"#64748b",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
          </div>

          {errors.api && <div style={{padding:"10px 14px",background:"rgba(220,38,38,.08)",border:"1px solid rgba(220,38,38,.2)",borderRadius:10,fontSize:12,color:"#dc2626",marginBottom:14}}>⚠️ {errors.api}</div>}
        </div>

        {/* Body — scrollable */}
        <div style={{flex:1,overflowY:"auto",padding:"0 24px 24px"}}>

          {/* Basic Info */}
          <SectionLabel>Basic Information</SectionLabel>
          <div className="fg">
            <label>Subteam Name {errors.name&&<span style={{color:"#dc2626",fontWeight:400}}>— {errors.name}</span>}</label>
            <input className="inp" placeholder="e.g. Sales East, Growth Team…" value={form.name}
              onChange={e=>{set("name",e.target.value);setErrors(x=>({...x,name:""}));}}
              style={errors.name?{borderColor:"#dc2626"}:{}}/>
          </div>
          <div className="fg">
            <label>Description <span style={{color:"#94a3b8",fontWeight:400}}>(optional)</span></label>
            <textarea className="inp" placeholder="What does this team focus on?" value={form.description}
              onChange={e=>set("description",e.target.value)} style={{minHeight:56,resize:"vertical"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div className="fg">
              <label>Lead <span style={{color:"#94a3b8",fontWeight:400}}>(optional)</span></label>
              <input className="inp" placeholder="e.g. Maria L." value={form.lead}
                onChange={e=>set("lead",e.target.value)}/>
            </div>
            <div className="fg">
              <label>Territory <span style={{color:"#94a3b8",fontWeight:400}}>(optional)</span></label>
              <input className="inp" placeholder="e.g. NYC · Boston" value={form.territory}
                onChange={e=>set("territory",e.target.value)}/>
            </div>
          </div>

          {/* Status (edit only) */}
          {isEdit && (
            <>
            <SectionLabel>Status</SectionLabel>
            <div className="fg">
              <select className="inp" value={form.status} onChange={e=>set("status",e.target.value)} style={{cursor:"pointer"}}>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            </>
          )}

          {/* Period */}
          <SectionLabel>Period</SectionLabel>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div className="fg">
              <label>Start Date</label>
              <input className="inp" type="date" value={form.period_start}
                onChange={e=>set("period_start",e.target.value)}/>
            </div>
            <div className="fg">
              <label>End Date {errors.period_end&&<span style={{color:"#dc2626",fontWeight:400}}>— {errors.period_end}</span>}</label>
              <input className="inp" type="date" value={form.period_end}
                onChange={e=>{set("period_end",e.target.value);setErrors(x=>({...x,period_end:""}));}}
                style={errors.period_end?{borderColor:"#dc2626"}:{}}/>
            </div>
          </div>

          {/* Targets */}
          <SectionLabel>Targets</SectionLabel>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {[
              {k:"target_taps",    lbl:"Taps",     col:"#0891b2", ico:"💳"},
              {k:"target_connects",lbl:"Connects", col:"#059669", ico:"🤝"},
              {k:"target_saves",   lbl:"Saves",    col:"#7c3aed", ico:"💾"},
              {k:"target_views",   lbl:"Views",    col:"#d97706", ico:"👁"},
            ].map(({k,lbl,col,ico})=>(
              <div className="fg" key={k}>
                <label style={{display:"flex",alignItems:"center",gap:5}}>
                  <span>{ico}</span> {lbl}
                </label>
                <input className="inp" type="number" min="0" step="1" value={form[k]}
                  onChange={e=>setNum(k,e.target.value)}
                  onKeyDown={e=>{if(e.key==='-'||e.key==='e')e.preventDefault();}}
                  style={{fontVariantNumeric:"tabular-nums"}}/>
              </div>
            ))}
          </div>

          {/* Icon + Color */}
          <SectionLabel>Team Identity</SectionLabel>
          <div className="fg">
            <label>Icon</label>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {TEAM_ICONS.map(ic=>(
                <div key={ic} onClick={()=>set("icon",ic)}
                  style={{width:36,height:36,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,cursor:"pointer",transition:"all .14s",
                    background:form.icon===ic?`${form.color}14`:"rgba(0,0,0,.03)",
                    border:form.icon===ic?`2px solid ${form.color}`:"2px solid transparent",
                    boxShadow:form.icon===ic?`0 2px 8px ${form.color}22`:"none"}}>{ic}</div>
              ))}
            </div>
          </div>
          <div className="fg">
            <label>Color</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {TEAM_COLORS.map(c=>(
                <div key={c} onClick={()=>set("color",c)}
                  style={{width:32,height:32,borderRadius:8,background:c,cursor:"pointer",transition:"all .14s",
                    border:form.color===c?"3px solid #0f172a":"3px solid transparent",
                    boxShadow:form.color===c?`0 4px 12px ${c}60`:"0 2px 6px rgba(0,0,0,.1)"}}/>
              ))}
            </div>
          </div>

          {/* Team Members */}
          <SectionLabel>
            Team Members <span style={{fontWeight:400,textTransform:"none"}}>({selectedMembers.length} selected)</span>
          </SectionLabel>
          <input className="inp" placeholder="Search members…" value={memberSearch}
            onChange={e=>setMemberSearch(e.target.value)} style={{marginBottom:10,fontSize:12}}/>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto",paddingRight:4}}>
            {filteredMembers.map(m=>{
              const sel = selectedMembers.includes(m.id);
              return (
                <div key={m.id} onClick={()=>toggleMember(m.id)}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,cursor:"pointer",
                    background:sel?`${form.color}0d`:"rgba(255,255,255,.7)",
                    border:`1.5px solid ${sel?form.color:"rgba(0,0,0,.07)"}`,
                    boxShadow:sel?`0 0 0 3px ${form.color}18`:"none",transition:"all .15s"}}>
                  {m.photo ? (
                    <img src={m.photo} alt="" style={{width:30,height:30,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>
                  ) : (
                    <Av name={m.name ? m.name.slice(0,2).toUpperCase() : "??"} grad={m.profile_color || "linear-gradient(150deg,#64748b,#059669)"} size={30}/>
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12.5,fontWeight:570,color:"#0f172a"}}>{m.name || "—"}</div>
                    <div style={{fontSize:11,color:"#94a3b8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.job_title || "—"} · {m.email}</div>
                  </div>
                  <div style={{width:20,height:20,borderRadius:6,flexShrink:0,
                    background:sel?form.color:"transparent",
                    border:`2px solid ${sel?form.color:"rgba(0,0,0,.18)"}`,
                    display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:11,color:"#fff",fontWeight:800,transition:"all .15s"}}>
                    {sel&&"✓"}
                  </div>
                </div>
              );
            })}
            {filteredMembers.length===0&&<div style={{padding:16,textAlign:"center",color:"#94a3b8",fontSize:12}}>No members found</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={{padding:"16px 24px",borderTop:"1px solid rgba(0,0,0,.07)",display:"flex",alignItems:"center",justifyContent:"flex-end",gap:9,flexShrink:0}}>
          <button className="btn bs" style={{padding:"9px 20px",fontSize:13}} onClick={onClose}>Cancel</button>
          <button className="btn bp" style={{padding:"9px 22px",fontSize:13,gap:6}} onClick={handleSave} disabled={saving}>
            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save Changes" : "Create Subteam")}
            {selectedMembers.length>0&&!saving&&<span style={{opacity:.75,fontSize:11}}>({selectedMembers.length})</span>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
});
